import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';

import API from 'core/Api';
import { createErrorFromResponse } from 'core/Error';

import { useActiveClientContext } from 'store/active-client';
import { useSelectableTableContext } from 'store/selectable-table';
import { usePermissionsContext } from 'store/permissions';
import { useModal } from 'store/modal';
import { PermissionKeys } from 'store/types';

import { buildClientUrl, buildClientEntity } from 'utils/helpers/client';
import { getSubdomain } from 'utils/helpers/routing';
import { getUserInfo } from 'utils/auth';
import { multiSignDocAsEmployer } from 'utils/helpers/helpers';
import { requestMultipleDownload, downloadMultipleFiles, extractErrorMessage } from 'utils/helpers/archives';

import MainLayout from 'components/main-layout/MainLayout';
import LayoutContent from 'components/main-layout/LayoutContent';
import TabNavigation from 'components/TabNavigation';
import NotAuthorizedScreen from 'routes/not-authorized';
import Button from 'components/Button';
import TooltipWrapper from 'components/TooltipWrapper';
import { FaFileSignature, FaDownload, FaTrash } from 'react-icons/fa';

interface IProps {
  isLoading?: boolean;
  error?: unknown;
  canMultiSign?: boolean;
  canMultiDownload?: boolean;
  canMultiDelete?: boolean;
  customSelectableProps?: string[];
  setCustomSelectableRowProps?: (val: string[]) => void;
  setMultiActionActive?: (active: boolean) => void;
  children: React.ReactNode;
}

const InnerWrapper: FC<IProps> = ({
  isLoading,
  error,
  canMultiSign,
  canMultiDownload,
  canMultiDelete,
  setCustomSelectableRowProps,
  setMultiActionActive,
  customSelectableProps,
  children,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { client } = useActiveClientContext();
  const { showModal, hideModal } = useModal();
  const { checkPermission } = usePermissionsContext();

  const { showSelectColumn, setShowSelectColumn, resetSelectable, selectedRowsOnAction } = useSelectableTableContext();

  const { featureHealthAndSafetyTraining } = getUserInfo();
  const isOperator = getSubdomain() === 'operators';

  const entityName = location.pathname.includes('/completed')
    ? buildClientEntity('health-and-safety-training-completed', client)
    : buildClientEntity('health-and-safety-training', client);

  useEffect(() => {
    /**
     * If we go between different tabs and then go back
     * to sign tab - reset active table select state.
     */
    resetSelectable();
    if (setMultiActionActive) {
      setMultiActionActive(false);
    }
  }, []);

  const showNoSelectedMsg = () =>
    showModal({
      type: 'error',
      content: t('documents.table.noDocsSelected'),
    });

  const getAllCachedItems = () => {
    const queries = queryClient.getQueriesData([entityName]);

    return queries.flatMap(([, data]: any) => data?.items || []);
  };

  const multiDelete = (docIds: number[], validationMessage?: string) => {
    return API.delete('/health-and-safety-training', {
      data: {
        health_and_safety_training_ids: docIds,
      },
    })
      .then(res => {
        const { message } = res.data;
        let successContent = '';

        if (message) {
          const { type, body } = message;
          if (type === 'multi_delete') {
            successContent = body;
          }
        }

        // Show mass action message first, then validation warning
        if (validationMessage && successContent) {
          successContent = `${successContent}\n\n${validationMessage}`;
        } else if (validationMessage) {
          successContent = validationMessage;
        }

        if (successContent || message) {
          showModal({
            type: 'success',
            content: successContent || message?.body,
          });
        } else {
          showModal({
            type: 'success',
            content: t('healthAndSafetyTraining.deletedSuccessMsg_plural'),
          });
        }
        queryClient.resetQueries(buildClientEntity('health-and-safety-training', client));
      })
      .catch(err =>
        showModal({
          type: 'error',
          content: createErrorFromResponse(err).message,
        })
      )
      .finally(() => resetSelectable());
  };

  const multiSign = async (rowIds: number[]) => {
    try {
      const allItems = getAllCachedItems();

      if (!allItems.length) {
        showModal({
          type: 'error',
          content: t('documents.table.noData'),
        });
        return;
      }

      const allIds = allItems
        .filter((item: any) => rowIds.includes(item.id))
        .map((item: any) => item.document_holder_id)
        .filter(Boolean);

      if (!allIds.length) {
        showModal({
          type: 'error',
          content: t('documents.table.noDocsSelected'),
        });
        return;
      }

      showModal({
        type: 'loading',
        heading: t('leaves.eSign'),
        content: t('leaves.eSignSubtitle'),
      });

      await multiSignDocAsEmployer(
        allIds,
        showModal,
        () => queryClient.resetQueries(buildClientEntity('documents-for-sign', client)),
        '',
        t
      ).then(async () => {
        await new Promise(resolve => setTimeout(resolve, 1500));
        queryClient.invalidateQueries(entityName);
        resetSelectable();
      });
    } catch (err) {
      resetSelectable();
      console.log('Error signing documents:', err);
      showModal({
        type: 'error',
        content: createErrorFromResponse(err).message,
      });
    }
  };

  const confirmSignFlow = (rowIds: number[]) => {
    if (rowIds.length) {
      showModal({
        type: 'confirm',
        content: t('healthAndSafetyTraining.confirmSignMsg'),
        onCancel: () => {
          resetSelectable();
          if (setMultiActionActive) {
            setMultiActionActive(false);
          }
        },
        confirmButton: (
          <Button
            className="confirm-btn"
            btnType="primary"
            onClick={() => {
              hideModal();
              multiSign(rowIds);
              if (setMultiActionActive) {
                setMultiActionActive(false);
              }
            }}
          >
            {t('common.confirm')}
          </Button>
        ),
      });
    } else {
      showNoSelectedMsg();
    }
  };

  const confirmBulkDownloadFlow = async (rowIds: number[]) => {
    if (rowIds.length === 0) {
      showNoSelectedMsg();
      return;
    }

    try {
      const allItems = getAllCachedItems();

      if (!allItems.length) {
        showModal({
          type: 'error',
          content: t('documents.table.noData'),
        });
        return;
      }

      const allIds = allItems
        .filter((item: any) => rowIds.includes(item.id))
        .map((item: any) => item.document_id)
        .filter(Boolean);

      if (!allIds.length) {
        showModal({
          type: 'error',
          content: t('documents.table.noDocsSelected'),
        });
        return;
      }

      showModal({
        type: 'loading',
        heading: t('documents.bulkDownload.preparing'),
        content: t('documents.bulkDownload.pleaseWait'),
      });

      const response = await requestMultipleDownload(allIds, 'health-and-safety-tab');

      if (response.type === 'immediate-download') {
        hideModal();
        showModal({
          type: 'success',
          content: t('documents.bulkDownload.immediateDownloadMessage'),
        });

        downloadMultipleFiles(response.links);

        resetSelectable();
        if (setMultiActionActive) {
          setMultiActionActive(false);
        }
      } else {
        hideModal();
        showModal({
          type: 'success',
          heading: t('documents.bulkDownload.archiveGenerating'),
          content: t('documents.bulkDownload.archiveMessage'),
        });

        resetSelectable();
        if (setMultiActionActive) {
          setMultiActionActive(false);
        }
      }
    } catch (err: any) {
      hideModal();
      resetSelectable();
      if (setMultiActionActive) {
        setMultiActionActive(false);
      }
      const errorMessage = extractErrorMessage(err, t('documents.bulkDownload.error'));
      showModal({
        type: 'error',
        content: errorMessage,
      });
    }
  };

  const confirmDeleteFlow = (rowIds: number[]) => {
    if (rowIds.length) {
      showModal({
        type: 'confirm',
        heading: t('healthAndSafetyTraining.confirmDeleteHeading'),
        content: t('healthAndSafetyTraining.confirmDeleteMsg'),
        onCancel: () => {
          resetSelectable();
          if (setMultiActionActive) {
            setMultiActionActive(false);
          }
        },
        confirmButton: (
          <Button
            className="confirm-btn"
            btnType="primary"
            onClick={() => {
              hideModal();
              multiDelete(rowIds);
              if (setMultiActionActive) {
                setMultiActionActive(false);
              }
            }}
          >
            {t('common.confirm')}
          </Button>
        ),
      });
    } else {
      showNoSelectedMsg();
    }
  };

  if (!featureHealthAndSafetyTraining && !isOperator) {
    return <NotAuthorizedScreen />;
  }

  if (isOperator && !client?.is_health_and_safety_training) {
    return <NotAuthorizedScreen />;
  }

  const tabs = [
    {
      to: '/health-and-safety-training',
      label: t('healthAndSafetyTraining.tabs.list'),
      entityName: buildClientEntity('health-and-safety-training', client),
      isExact: true,
    },
    {
      to: '/health-and-safety-training/completed',
      label: t('healthAndSafetyTraining.tabs.completedList'),
      entityName: buildClientEntity('health-and-safety-training-completed', client),
      isExact: true,
    },
    {
      onRefresh: () => {
        const currentTab = tabs.find(tab => tab.to && buildClientUrl(tab.to, client) === location.pathname);
        if (currentTab?.entityName) {
          queryClient.resetQueries(currentTab.entityName);
        }
      },
    },
  ];

  return (
    <MainLayout title={t('healthAndSafetyTraining.title')}>
      <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-md-center">
        <div>
          <TabNavigation tabs={tabs} />
        </div>
        {(canMultiSign || canMultiDownload) && !showSelectColumn && (
          <MultiActionWrapper>
            <StyledH5>{t('leaves.table.multiActions')}:</StyledH5>

            <ButtonsWrapper>
              {canMultiSign && checkPermission(PermissionKeys.CAN_MULTI_SIGN) && (
                <Button
                  className="ml-2 mb-3"
                  btnType="info"
                  onClick={() => {
                    if (setCustomSelectableRowProps) setCustomSelectableRowProps([PermissionKeys.CAN_MULTI_SIGN]);
                    setShowSelectColumn(true);
                    if (setMultiActionActive) {
                      setMultiActionActive(true);
                    }
                  }}
                >
                  <FaFileSignature className="mr-2" />
                  {t('leaves.table.signing')}
                </Button>
              )}
              {(canMultiSign || canMultiDownload) && (
                <Button
                  className="ml-2 mb-3"
                  btnType="secondary"
                  onClick={() => {
                    if (setCustomSelectableRowProps) setCustomSelectableRowProps(['CAN_MULTI_DOWNLOAD']);
                    setShowSelectColumn(true);
                    if (setMultiActionActive) {
                      setMultiActionActive(true);
                    }
                  }}
                >
                  <TooltipWrapper
                    id="tooltip-mass-download"
                    style={{ width: '100%' }}
                    overlay={t('documents.bulkDownload.tooltip')}
                  >
                    <FaDownload className="mr-2" />
                    {t('documents.table.download')}
                  </TooltipWrapper>
                </Button>
              )}
              {canMultiDelete && checkPermission(PermissionKeys.CAN_MULTI_DELETE) && (
                <Button
                  className="ml-2 mb-3"
                  btnType="danger"
                  onClick={() => {
                    if (setCustomSelectableRowProps) setCustomSelectableRowProps([PermissionKeys.CAN_MULTI_DELETE]);
                    setShowSelectColumn(true);
                    if (setMultiActionActive) {
                      setMultiActionActive(true);
                    }
                  }}
                >
                  <FaTrash className="mr-2" />
                  {t('documents.table.delete')}
                </Button>
              )}
            </ButtonsWrapper>
          </MultiActionWrapper>
        )}
        {(canMultiSign || canMultiDownload || canMultiDelete) && showSelectColumn && (
          <div className="mb-3">
            {canMultiSign && customSelectableProps?.includes(PermissionKeys.CAN_MULTI_SIGN) && (
              <Button
                className="ml-2"
                btnType="info"
                onClick={() => selectedRowsOnAction(confirmSignFlow)}
                preventDebounce
              >
                <FaFileSignature
                  size={14}
                  className="mr-2"
                />
                {t('leaves.table.sign')}
              </Button>
            )}
            {customSelectableProps?.includes('CAN_MULTI_DOWNLOAD') && (
              <Button
                className="ml-2"
                btnType="secondary"
                onClick={() => selectedRowsOnAction(confirmBulkDownloadFlow)}
                preventDebounce
              >
                <FaDownload
                  size={14}
                  className="mr-2"
                />
                {t('documents.table.download')}
              </Button>
            )}
            {canMultiDelete && customSelectableProps?.includes(PermissionKeys.CAN_MULTI_DELETE) && (
              <Button
                className="ml-2"
                btnType="danger"
                onClick={() => selectedRowsOnAction(confirmDeleteFlow)}
                preventDebounce
              >
                <FaTrash
                  size={14}
                  className="mr-2"
                />
                {t('documents.table.delete')}
              </Button>
            )}
            <Button
              className="ml-2"
              btnType="light"
              outlined
              onClick={() => {
                resetSelectable();
                if (setMultiActionActive) {
                  setMultiActionActive(false);
                }
              }}
            >
              {t('common.cancel')}
            </Button>
          </div>
        )}
      </div>

      <LayoutContent
        isLoading={isLoading}
        error={error}
      >
        {children}
      </LayoutContent>
    </MainLayout>
  );
};

export default InnerWrapper;

const MultiActionWrapper = styled.div`
  display: block;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 0.5rem;

  button {
    margin-left: 0 !important;
    margin-bottom: 0 !important;
    white-space: nowrap;
  }

  @media ${props => props.theme.breakpoints.md} {
    flex-wrap: wrap;

    button {
      flex: 1 1 auto;
      min-width: 150px;
    }
  }

  @media ${props => props.theme.breakpoints.sm} {
    button {
      flex: 1 1 100%;
    }
  }
`;

const StyledH5 = styled.h5`
  text-align: right;
  padding-right: 5px;
  position: relative;
  margin: 0 0 0.5rem 0;
  white-space: nowrap;

  @media ${props => props.theme.breakpoints.md} {
    text-align: center;
  }
`;
